import type { RadioItem } from '@affine/component';
import { RadioGroup, Switch } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { LanguageMenu } from '@affine/core/components/affine/language-menu';
import { TraySettingService } from '@affine/core/modules/editor-setting/services/tray-settings';
import { useUiSettingsVaultSyncEnabled } from '@affine/core/modules/ui-settings-vault';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useTheme } from 'next-themes';
import { useCallback, useMemo } from 'react';

import { useAppSettingHelper } from '../../../../../components/hooks/affine/use-app-setting-helper';
// import { OpenInAppLinksMenu } from './links'; // StreamLain: No desktop app yet
import { settingWrapper, themeSettingWrapper } from './style.css';
// import { ThemeEditorSetting } from './theme-editor-setting';

export const getThemeOptions = (t: ReturnType<typeof useI18n>) =>
  [
    {
      value: 'system',
      label: t['com.streamlain.themeSettings.system'](),
      testId: 'system-theme-trigger',
    },
    {
      value: 'light',
      label: t['com.streamlain.themeSettings.light'](),
      testId: 'light-theme-trigger',
    },
    {
      value: 'dark',
      label: t['com.streamlain.themeSettings.dark'](),
      testId: 'dark-theme-trigger',
    },
    {
      value: 'blue-white',
      label: t['com.streamlain.themeSettings.blue'](),
      testId: 'blue-theme-trigger',
    },
  ] satisfies RadioItem[];

export const ThemeSettings = () => {
  const t = useI18n();
  const { setTheme, theme } = useTheme();

  const radioItems = useMemo<RadioItem[]>(() => getThemeOptions(t), [t]);

  return (
    <RadioGroup
      items={radioItems}
      value={theme}
      gap={8}
      className={themeSettingWrapper}
      onChange={useCallback(
        (value: string) => {
          setTheme(value);
        },
        [setTheme]
      )}
    />
  );
};

const MenubarSetting = () => {
  const t = useI18n();
  const traySettingService = useService(TraySettingService);
  const traySetting = useLiveData(traySettingService.settings$);

  return (
    <>
      <SettingWrapper
        id="menubar"
        title={t['com.streamlain.appearanceSettings.menubar.title']()}
      >
        <SettingRow
          name={t['com.streamlain.appearanceSettings.menubar.toggle']()}
          desc={t['com.streamlain.appearanceSettings.menubar.description']()}
        >
          <Switch
            checked={traySetting.enabled}
            onChange={checked => traySettingService.setEnabled(checked)}
          />
        </SettingRow>
      </SettingWrapper>
      {traySetting.enabled && !environment.isMacOs ? (
        <SettingWrapper
          id="windowBehavior"
          title={t[
            'com.streamlain.appearanceSettings.menubar.windowBehavior.title'
          ]()}
        >
          <SettingRow
            name={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.openOnLeftClick.toggle'
            ]()}
            desc={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.openOnLeftClick.description'
            ]()}
          >
            <Switch
              checked={traySetting.openOnLeftClick}
              onChange={checked =>
                traySettingService.setOpenOnLeftClick(checked)
              }
            />
          </SettingRow>
          <SettingRow
            name={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.minimizeToTray.toggle'
            ]()}
            desc={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.minimizeToTray.description'
            ]()}
          >
            <Switch
              checked={traySetting.minimizeToTray}
              onChange={checked =>
                traySettingService.setMinimizeToTray(checked)
              }
            />
          </SettingRow>
          <SettingRow
            name={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.closeToTray.toggle'
            ]()}
            desc={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.closeToTray.description'
            ]()}
          >
            <Switch
              checked={traySetting.closeToTray}
              onChange={checked => traySettingService.setCloseToTray(checked)}
            />
          </SettingRow>
          <SettingRow
            name={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.startMinimized.toggle'
            ]()}
            desc={t[
              'com.streamlain.appearanceSettings.menubar.windowBehavior.startMinimized.description'
            ]()}
          >
            <Switch
              checked={traySetting.startMinimized}
              onChange={checked =>
                traySettingService.setStartMinimized(checked)
              }
            />
          </SettingRow>
        </SettingWrapper>
      ) : null}
    </>
  );
};

export const AppearanceSettings = () => {
  const t = useI18n();

  const { appSettings, updateSettings } = useAppSettingHelper();
  const [uiSettingsSyncEnabled, setUiSettingsSyncEnabled] =
    useUiSettingsVaultSyncEnabled();

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.appearanceSettings.title']()}
        subtitle={t['com.streamlain.appearanceSettings.subtitle']()}
      />

      <SettingWrapper
        title={t['com.streamlain.appearanceSettings.theme.title']()}
      >
        <SettingRow
          name={t['com.streamlain.appearanceSettings.color.title']()}
          desc={t['com.streamlain.appearanceSettings.color.description']()}
          data-testid="appearance-theme-row"
        >
          <ThemeSettings />
        </SettingRow>
        <SettingRow
          name={t['com.streamlain.appearanceSettings.language.title']()}
          desc={t['com.streamlain.appearanceSettings.language.description']()}
          data-testid="appearance-language-row"
        >
          <div className={settingWrapper}>
            <LanguageMenu />
          </div>
        </SettingRow>
        {BUILD_CONFIG.isElectron ? (
          <SettingRow
            name={t['com.streamlain.appearanceSettings.clientBorder.title']()}
            desc={t[
              'com.streamlain.appearanceSettings.clientBorder.description'
            ]()}
            data-testid="client-border-style-trigger"
          >
            <Switch
              checked={appSettings.clientBorder}
              onChange={checked => updateSettings('clientBorder', checked)}
            />
          </SettingRow>
        ) : null}
        <SettingRow
          name={t['com.streamlain.appearanceSettings.sync.title']()}
          desc={t['com.streamlain.appearanceSettings.sync.description']()}
          data-testid="ui-settings-sync-row"
        >
          <Switch
            checked={uiSettingsSyncEnabled}
            onChange={setUiSettingsSyncEnabled}
          />
        </SettingRow>
        {/* StreamLain: Theme editor hidden until the customization flow is ready
        {enableThemeEditor ? <ThemeEditorSetting /> : null}
        */}
      </SettingWrapper>

      {/* StreamLain: No desktop app yet — hide open-in-app settings.
          Restore when desktop app is available.
      {BUILD_CONFIG.isWeb && !environment.isMobile ? (
        <SettingWrapper title={t['com.streamlain.setting.appearance.links']()}>
          <SettingRow
            name={t['com.streamlain.setting.appearance.open-in-app']()}
            desc={t['com.streamlain.setting.appearance.open-in-app.hint']()}
            data-testid="open-in-app-links-trigger"
          >
            <OpenInAppLinksMenu />
          </SettingRow>
        </SettingWrapper>
      ) : null}
      */}

      {BUILD_CONFIG.isElectron ? (
        <SettingWrapper
          title={t['com.streamlain.appearanceSettings.sidebar.title']()}
        >
          <SettingRow
            name={t[
              'com.streamlain.appearanceSettings.noisyBackground.title'
            ]()}
            desc={t[
              'com.streamlain.appearanceSettings.noisyBackground.description'
            ]()}
          >
            <Switch
              checked={appSettings.enableNoisyBackground}
              onChange={checked =>
                updateSettings('enableNoisyBackground', checked)
              }
            />
          </SettingRow>
          {environment.isMacOs ? (
            <SettingRow
              name={t[
                'com.streamlain.appearanceSettings.translucentUI.title'
              ]()}
              desc={t[
                'com.streamlain.appearanceSettings.translucentUI.description'
              ]()}
            >
              <Switch
                checked={appSettings.enableBlurBackground}
                onChange={checked =>
                  updateSettings('enableBlurBackground', checked)
                }
              />
            </SettingRow>
          ) : null}
          {/* StreamLain: linked pages in sidebar defaults off and UI is hidden
        <SettingRow
          name={t[
            'com.streamlain.appearanceSettings.showLinkedDocInSidebar.title'
          ]()}
          desc={t[
            'com.streamlain.appearanceSettings.showLinkedDocInSidebar.description'
          ]()}
        >
          <Switch
            checked={!!appSettings.showLinkedDocInSidebar}
            onChange={checked =>
              updateSettings('showLinkedDocInSidebar', checked)
            }
          />
        </SettingRow>
        */}
        </SettingWrapper>
      ) : null}

      {BUILD_CONFIG.isElectron ? <MenubarSetting /> : null}
    </>
  );
};
